import { apiRequest } from "util/services";
import config from "Configurations";

const BASE = "feedback";

function isUnavailable(res) {
  return res?.database_ready === false || res?.error?.includes?.("not available");
}

/** Public API base for capture.js data-api attribute (matches current environment). */
export function feedbackPublicApiBase() {
  const raw = (config.BASE_URL || "https://api.pivony.com/api/v1/").replace(/\/+$/, "");
  if (raw.endsWith("/api/v1")) return raw;
  if (raw.endsWith("/api")) return raw;
  return raw;
}

/** capture.js host — production/staging CDN; demo mirror only while DNS propagates. */
export function captureCdnScriptUrl() {
  if (process.env.REACT_APP_CAPTURE_CDN) {
    return `${String(process.env.REACT_APP_CAPTURE_CDN).replace(/\/+$/, "")}/capture.js`;
  }
  return "https://cdn.pivony.com/capture.js";
}

export async function checkFeedbackApiReady() {
  const res = await apiRequest(`${BASE}/instruments`, "GET", null, true);
  if (isUnavailable(res)) {
    return { ready: false, reason: "migration" };
  }
  if (res?.error && !res?.instruments) {
    return { ready: false, reason: "error" };
  }
  return { ready: true };
}

export function mapInstrumentToWidget(inst) {
  const cfg = inst?.config || {};
  const channels = inst?.channels || [];
  return {
    id: inst.id,
    name: inst.title || "",
    description: inst.description || "",
    status: inst.status || "draft",
    closedByPlan: inst.closed_by_plan === true,
    createdAt: inst.created_at,
    updatedAt: inst.updated_at,
    prompt: cfg.prompt || "",
    modes: Array.isArray(cfg.modes) ? cfg.modes : ["voice", "text"],
    maxDurationSeconds: cfg.max_duration_seconds || 120,
    enabledChannels: inst.enabled_channels || [],
    channels,
    config: cfg,
    type: inst.type,
    knowledge_stats: inst.knowledge_stats || null,
  };
}

export function widgetToInstrumentPayload(widget, type = "ai_voice") {
  return {
    type,
    title: widget.name,
    description: widget.description || null,
    status: widget.status || "draft",
    config: {
      prompt: widget.prompt || "",
      modes: widget.modes || ["voice", "text"],
      max_duration_seconds: widget.maxDurationSeconds || 120,
      ...(widget.config || {}),
    },
  };
}

export async function listInstruments(type) {
  const { items } = await listInstrumentsWithLimits(type);
  return items;
}

export async function listInstrumentsWithLimits(type) {
  const res = await apiRequest(`${BASE}/instruments`, "GET", null, true);
  if (isUnavailable(res)) {
    const err = new Error("FEEDBACK_UNAVAILABLE");
    err.unavailable = true;
    throw err;
  }
  let items = res?.instruments || [];
  if (type) items = items.filter((i) => i.type === type);
  return {
    items: items.map(mapInstrumentToWidget),
    prospect_limits: res?.prospect_limits || null,
  };
}

export async function getInstrument(id) {
  const res = await apiRequest(`${BASE}/instruments/${id}`, "GET", null, true);
  if (isUnavailable(res) || !res?.instrument) {
    throw new Error("Instrument not found");
  }
  return mapInstrumentToWidget(res.instrument);
}

export async function createInstrument(payload) {
  const res = await apiRequest(`${BASE}/instruments`, "POST", payload, true);
  if (isUnavailable(res) || !res?.instrument) {
    throw new Error(res?.error || "Create failed");
  }
  return mapInstrumentToWidget(res.instrument);
}

export async function updateInstrument(id, payload) {
  const res = await apiRequest(`${BASE}/instruments/${id}`, "PUT", payload, true);
  if (isUnavailable(res) || !res?.instrument) {
    throw new Error(res?.error || "Update failed");
  }
  return mapInstrumentToWidget(res.instrument);
}

export async function setInstrumentStatus(id, status) {
  const res = await apiRequest(
    `${BASE}/instruments/${id}/status`,
    "POST",
    { status },
    true,
  );
  if (isUnavailable(res) || !res?.instrument) {
    throw new Error(res?.error || "Status update failed");
  }
  return mapInstrumentToWidget(res.instrument);
}

export async function deleteInstrument(id) {
  const res = await apiRequest(`${BASE}/instruments/${id}`, "DELETE", null, true);
  if (isUnavailable(res)) {
    throw new Error(res?.error || "Delete failed");
  }
  return true;
}

export async function upsertChannels(id, channels) {
  const res = await apiRequest(
    `${BASE}/instruments/${id}/channels`,
    "PUT",
    { channels },
    true,
  );
  if (isUnavailable(res) || !res?.instrument) {
    throw new Error(res?.error || "Channel update failed");
  }
  return mapInstrumentToWidget(res.instrument);
}

export function buildCaptureEmbedSnippet(slug, { modes, displayMode, prompt }) {
  const modeStr = (modes || ["voice", "text"]).join(",");
  const question = (prompt || "").replace(/"/g, "&quot;");
  const apiBase = feedbackPublicApiBase();
  const needsApiAttr =
    !apiBase.includes("api.pivony.com/api/v1") &&
    !apiBase.includes("app.pivony.com");
  return (
    `<script src="${captureCdnScriptUrl()}"\n` +
    `  data-capture="${slug}"\n` +
    `  data-modes="${modeStr}"\n` +
    `  data-mode="${displayMode || "popup"}"` +
    (question ? `\n  data-question="${question}"` : "") +
    (needsApiAttr ? `\n  data-api="${apiBase}"` : "") +
    `></script>`
  );
}

export function channelSlug(widget, channelType) {
  const ch = (widget?.channels || []).find((c) => c.channel_type === channelType);
  return ch?.slug || ch?.config?.slug || null;
}

export function channelUrl(widget, channelType) {
  const ch = (widget?.channels || []).find((c) => c.channel_type === channelType);
  return ch?.config?.full_url || null;
}

export async function getFeatureWaitlistStatus(feature) {
  const res = await apiRequest(
    `${BASE}/waitlist?feature=${encodeURIComponent(feature)}`,
    "GET",
    null,
    true,
  );
  if (isUnavailable(res)) {
    return { joined: false, unavailable: true };
  }
  return { joined: !!res?.joined, unavailable: false };
}

export async function joinFeatureWaitlist(feature) {
  const res = await apiRequest(
    `${BASE}/waitlist`,
    "POST",
    { feature },
    true,
  );
  if (isUnavailable(res)) {
    throw new Error("WAITLIST_UNAVAILABLE");
  }
  if (res?.error && !res?.joined) {
    throw new Error(res.error);
  }
  return { joined: true, created: !!res?.created };
}

export async function listInstrumentResponses(instrumentId) {
  const res = await apiRequest(
    `${BASE}/instruments/${instrumentId}/responses`,
    "GET",
    null,
    true,
  );
  if (isUnavailable(res)) {
    throw new Error("FEEDBACK_UNAVAILABLE");
  }
  if (res?.error) {
    throw new Error(res.error);
  }
  return res?.responses || [];
}

// Phone responses' conversations by response id: transcript lines and question / answer pairs.
// With a responseId only that response's (the drawer); without, every phone response (the export).
export async function listResponseConversations(instrumentId, responseId) {
  const query = responseId ? `?response_id=${encodeURIComponent(responseId)}` : "";
  const res = await apiRequest(
    `${BASE}/instruments/${instrumentId}/conversations${query}`,
    "GET",
    null,
    true,
  );
  if (!res || res.error) {
    throw new Error(res?.error || "CONVERSATIONS_UNAVAILABLE");
  }
  return res.conversations || {};
}

// A phone response's call audio (MP3) as a Blob, or null. The recording is private to the
// org, so it is fetched with the session token rather than linked.
export async function fetchResponseRecording(instrumentId, responseId) {
  let token = null;
  try {
    token = JSON.parse(window.localStorage.getItem("jwt_token") || "{}").value || null;
  } catch (_) {
    token = null;
  }
  const res = await fetch(
    `${config.BASE_URL}${BASE}/instruments/${instrumentId}/responses/${responseId}/recording`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  if (!res.ok) return null;
  return res.blob();
}
